import {
    Button,
    List,
    ListItem,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import moment from "moment";
import { MouseEvent, useState } from "react";
import CollapsibleSection from "../../../components/collapsible-section/collapsible-section";
import DownloadLocations from "../../../components/download-locations/download-locations";
import { displayDateFormat } from "../../../constants";
import { downloadListSnapshotLocationsAction } from "../../../features/download/redux/download.actions";
import { IRootState } from "../../../redux/root.reducer";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { hasAccessToHistoricalSnapshots } from "../../../service/user-permission";
import SnapshotAccessDialog from "../../../shared/component/snapshot-access-dialog";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { historicalSnapshotExportSummaryAction } from "../redux/historical-snapshots/historical-snapshots.actions";

const ExportButton = ({ onExportClick }: { onExportClick: () => void }) => (
    <Button
        variant="outlined"
        color="secondary"
        onClick={(e) => {
            e.stopPropagation();
            onExportClick();
        }}
    >
        Export Historical Summary
    </Button>
);

const HistoricalSnapshots = ({ className }: IWithClassName) => {
    const { details } = useAppSelector(({ listPage }) => listPage.listDetails);
    const { snapshots, isLoading } = useAppSelector(
        ({ listPage }) => listPage.historicalSnapshot,
    );
    const { isLoading: isLoadingListDetails } = useAppSelector(
        ({ listPage }) => listPage.listDetails,
    );
    const role = useAppSelector(({ auth }: IRootState) => auth.user?.role);
    const dispatch = useAppDispatch();
    const isAuthorized = hasAccessToHistoricalSnapshots(role);
    const [openDialog, setOpenDialog] = useState(false);

    const handleLockClick = () => setOpenDialog(true);

    const handleCloseDialog = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setOpenDialog(false);
    };

    const handleExportClick = () => {
        dispatch(historicalSnapshotExportSummaryAction());
    };

    const handleSectionClick = () => {
        if (!isAuthorized) {
            handleLockClick();
        }
    };

    return (
        <div onClick={handleSectionClick}>
            <CollapsibleSection
                className={className}
                title="Historical Snapshots"
                titleComponent={
                    isAuthorized && snapshots?.length ? (
                        <ExportButton onExportClick={handleExportClick} />
                    ) : (
                        <></>
                    )
                }
                collapsible={true}
                collapsed={true}
                isLoading={isLoading || isLoadingListDetails}
                hasAccess={isAuthorized}
            >
                {isAuthorized ? (
                    !snapshots?.length ? (
                        <Typography className="empty-list-message">
                            Please see associated Sublists for Historical
                            Snapshots.
                        </Typography>
                    ) : (
                        <>
                            <ListItem className="snapshot-title">
                                <Typography className="snapshot-date">
                                    Snapshot Date
                                </Typography>
                                <Typography className="snapshot-location">
                                    Locations
                                </Typography>
                                <Typography className="snapshot-country">
                                    Countries
                                </Typography>
                                <Typography className="snapshot-download">
                                    Download
                                </Typography>
                            </ListItem>
                            <List>
                                {snapshots?.map((snapshot) => (
                                    <ListItem
                                        key={snapshot.date}
                                        className="snapshot-item"
                                    >
                                        <Typography className="snapshot-date">
                                            {moment
                                                .utc(snapshot.date)
                                                .format(displayDateFormat)}
                                        </Typography>
                                        <Typography className="snapshot-location">
                                            {snapshot.records}
                                            {snapshot.covid && (
                                                <span className="covid-text">
                                                    COVID-19
                                                </span>
                                            )}
                                        </Typography>
                                        <div className="snapshot-country">
                                            <Tooltip
                                                title={snapshot.country.join(
                                                    ", ",
                                                )}
                                            >
                                                <span>
                                                    {snapshot.country.length ||
                                                        0}
                                                </span>
                                            </Tooltip>
                                        </div>
                                        {details && (
                                            <DownloadLocations
                                                downloadAction={() =>
                                                    downloadListSnapshotLocationsAction(
                                                        details.listId,
                                                        snapshot.date,
                                                    )
                                                }
                                            />
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )
                ) : null}
            </CollapsibleSection>
            <SnapshotAccessDialog
                open={openDialog}
                onClose={handleCloseDialog}
            />
        </div>
    );
};

export default styled(HistoricalSnapshots)(({ theme }: IWithThemeProps) => ({
    paddingTop: theme?.spacing(3),
    fontSize: 20,
    color: theme?.palette.announcement.primaryColor,

    ".MuiListItem-root": {
        color: theme?.palette.historicalSnapshsot.textColor,
        padding: theme?.spacing(4, 0),
        height: 80,
        borderBottom: `1px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }  `,
    },
    ".MuiListItem-root:last-child": {
        border: "none",
    },
    ".snapshot-title": {
        p: {
            fontWeight: 600,
        },
    },
    ".snapshot-date": {
        flexBasis: "40%",
        paddingLeft: theme?.spacing(5),
    },
    ".snapshot-location": {
        flexBasis: "30%",
    },
    ".covid-text": {
        paddingLeft: theme?.spacing(10),
        color: theme?.palette.detailsPane.covidTextColor,
    },
    ".snapshot-download": {
        flexBasis: "10%",
        textAlign: "right",
        paddingRight: theme?.spacing(4),
    },
    ".MuiDialogContentText-root": {
        textDecoration: "none",
    },
    ".empty-list-message": {
        padding: theme?.spacing(2),
    },
    ".snapshot-country": {
        flexBasis: "20%",
        cursor: "pointer",
        fontSize: "1rem",
    },
}));
